import React, { useRef, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { toPng } from 'html-to-image';
import { X, Download, Facebook, Twitter, Link as LinkIcon, Loader2 } from 'lucide-react';
import { Button } from './ui/button';
import { useToast } from '../hooks/use-toast';

const EMOJI_CHAR = {
  thumbs_up: '👍', thumbs_down: '👎', heart: '❤️',
  fire: '🔥', eggplant: '🍆', splash: '💦',
};

const proxyUrl = (url) => {
  if (!url) return '';
  if (url.includes('mecklenburgcountync.gov')) {
    return `${process.env.REACT_APP_BACKEND_URL}/api/mugshots/image-proxy?url=${encodeURIComponent(url)}`;
  }
  return url;
};

const firstName = (name) =>
  name?.includes(',') ? name.split(',')[1].trim().split(' ')[0] : (name || '').split(' ')[0];

const SHARE_URL = 'https://jailbirdsgame.com?ref=hall-of-shame';

const HallOfShameModal = ({ record, onClose }) => {
  const { toast } = useToast();
  const cardRef = useRef(null);
  const [generating, setGenerating] = useState(false);

  if (!record) return null;

  const name = firstName(record.name);
  const shareText = `🚨 HALL OF SHAME 🚨 ${name} is one of the MOST-REACTED mugshots on JailBirds! 🐦🔥 Think you can guess their crime? Play free 👉`;

  const generateImage = async () => {
    if (!cardRef.current) return null;
    return toPng(cardRef.current, { cacheBust: true, pixelRatio: 2, backgroundColor: '#0b0f17' });
  };

  const handleDownload = async () => {
    setGenerating(true);
    try {
      const dataUrl = await generateImage();
      if (dataUrl) {
        const link = document.createElement('a');
        link.download = `jailbirds-hall-of-shame-${name}.png`;
        link.href = dataUrl;
        link.click();
        toast({ title: 'Image saved! 📸', description: 'Post it to the JailBirds Facebook group!' });
      }
    } catch (e) {
      toast({ title: 'Could not generate image', description: 'Try the share buttons instead.', variant: 'destructive' });
    } finally {
      setGenerating(false);
    }
  };

  const handleNativeShareImage = async () => {
    setGenerating(true);
    try {
      const dataUrl = await generateImage();
      const blob = await (await fetch(dataUrl)).blob();
      const file = new File([blob], `jailbirds-${name}.png`, { type: 'image/png' });
      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        await navigator.share({
          files: [file],
          title: 'JailBirds Hall of Shame',
          text: `${shareText} ${SHARE_URL}`,
        });
      } else {
        handleDownload();
      }
    } catch (e) {
      if (e.name !== 'AbortError') handleDownload();
    } finally {
      setGenerating(false);
    }
  };

  const handleFacebook = () => {
    window.open(
      `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(SHARE_URL)}&quote=${encodeURIComponent(shareText)}`,
      '_blank', 'width=550,height=420'
    );
    toast({ title: 'Opening Facebook', description: 'Tip: download the image first to attach the mugshot!' });
  };

  const handleTwitter = () => {
    window.open(
      `https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}&url=${encodeURIComponent(SHARE_URL)}`,
      '_blank', 'width=550,height=420'
    );
  };

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(`${shareText} ${SHARE_URL}`);
      toast({ title: 'Copied!', description: 'Caption copied to clipboard.' });
    } catch (e) {
      toast({ title: 'Error', description: 'Could not copy.', variant: 'destructive' });
    }
  };

  const topEmojis = Object.entries(record.emoji_counts || {})
    .sort((a, b) => b[1] - a[1])
    .filter(([, c]) => c > 0)
    .slice(0, 4);

  return (
    <AnimatePresence>
      <div className="fixed inset-0 z-[200] flex items-center justify-center p-4">
        <motion.div
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
          onClick={onClose}
          className="absolute inset-0 bg-black/70 backdrop-blur-sm"
        />
        <motion.div
          initial={{ opacity: 0, scale: 0.92, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.92, y: 20 }}
          transition={{ type: 'spring', stiffness: 300, damping: 28 }}
          className="relative w-full max-w-sm"
          data-testid="hall-of-shame-modal"
        >
          <button
            onClick={onClose}
            data-testid="hall-of-shame-close"
            className="absolute -top-3 -right-3 z-10 p-2 rounded-full bg-card border border-border hover:bg-muted transition-colors"
          >
            <X className="w-4 h-4" />
          </button>

          {/* Shareable card */}
          <div
            ref={cardRef}
            className="rounded-2xl overflow-hidden border-2 border-orange-500/50 bg-[#0b0f17]"
          >
            <div className="bg-gradient-to-r from-orange-600 to-red-600 px-4 py-2.5 text-center">
              <div className="text-white font-heading font-extrabold tracking-widest text-sm">
                🚨 HALL OF SHAME 🚨
              </div>
            </div>

            <div className="relative aspect-square">
              <img
                src={proxyUrl(record.mugshot_url)}
                alt={record.name}
                crossOrigin="anonymous"
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/85 to-transparent p-3">
                <div className="text-white font-heading font-bold text-xl leading-tight">{name}</div>
                <div className="text-orange-300 text-xs font-semibold">
                  {record.total_reactions} reactions • Most Reacted on JailBirds
                </div>
              </div>
            </div>

            <div className="flex items-center justify-center gap-3 px-4 py-3 bg-card/40">
              {topEmojis.map(([emoji, count]) => (
                <div key={emoji} className="flex items-center gap-1 text-sm font-semibold text-foreground">
                  <span className="text-lg leading-none">{EMOJI_CHAR[emoji]}</span>
                  {count}
                </div>
              ))}
            </div>

            <div className="text-center py-2 bg-gradient-to-r from-cyan-500/10 to-cyan-400/5 border-t border-border">
              <span className="text-xs font-bold text-cyan-400">🐦 Guess the crime at JailBirdsGame.com</span>
            </div>
          </div>

          {/* Share actions */}
          <div className="mt-4 space-y-2">
            <Button
              onClick={navigator.share ? handleNativeShareImage : handleDownload}
              disabled={generating}
              data-testid="hall-of-shame-share-image"
              className="w-full bg-gradient-primary hover-glow-primary text-primary-foreground font-semibold"
            >
              {generating ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Download className="w-4 h-4 mr-2" />}
              {navigator.share ? 'Share Mugshot Card' : 'Download Card Image'}
            </Button>
            <div className="grid grid-cols-3 gap-2">
              <Button variant="outline" onClick={handleFacebook} data-testid="hall-of-shame-facebook" className="border-blue-500/40 hover:bg-blue-500/10">
                <Facebook className="w-4 h-4 text-blue-400" />
              </Button>
              <Button variant="outline" onClick={handleTwitter} data-testid="hall-of-shame-twitter">
                <Twitter className="w-4 h-4" />
              </Button>
              <Button variant="outline" onClick={handleCopy} data-testid="hall-of-shame-copy">
                <LinkIcon className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </motion.div>
      </div>
    </AnimatePresence>
  );
};

export default HallOfShameModal;
